import cn from 'classnames';
import { FC } from 'react';
import { useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { scrollToElementByID } from '@/shared/lib';

import {
  IOXRayImagesInterfaceModel,
  IOXrayImageInterface,
  groupIOXRayImagesByPartition,
} from '@/entities/IOXRayImagesMatrix';
import { organizationModel } from '@/entities/organization';
import { FMXNavigationMode, reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import { RenderViewSlider } from '@/features/renderViewSlider';

import styles from './IOXRayImageSlider.module.scss';

type IOXRayImageSliderProps = {
  className?: string;
};

export const IOXRayImageSlider: FC<IOXRayImageSliderProps> = (props) => {
  const { className } = props;

  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useMedia();

  const currentReport = useAppSelector(
    reportsModel.selectors.selectCurrentReport,
  );

  const navigationMode = useAppSelector(
    reportsModel.selectors.selectNavigationMode,
  );

  const reportID = currentReport?.ID;

  const { isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const IOXRayImagesInterface = useAppSelector(
    IOXRayImagesInterfaceModel.selectors.selectIOXRayImagesInterfaceByReportID,
  );

  const groupedIOXRayImages = groupIOXRayImagesByPartition(
    IOXRayImagesInterface as IOXrayImageInterface[],
  );

  const imageMetaIDs = Object.values(groupedIOXRayImages).flatMap(
    (imageItems) =>
      imageItems.map(
        (imageItem) =>
          imageItem.imageMeta?.GeneratedAssetID ||
          imageItem.imageMeta?.StudyAssetID,
      ),
  );

  const focusedImageMetaID = useAppSelector(
    reportsModel.selectors.selectFocusedImageMetaID,
  );

  const teethIDs = useAppSelector(
    toothModel.selectors.selectTeethIDsToShow(
      reportID as string,
      isFDAVersion2,
    ),
  );

  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );

  const isFMXMatrixViewMode = navigationMode === FMXNavigationMode.MatrixView;

  const itemIDs = isFMXMatrixViewMode ? imageMetaIDs : teethIDs;
  const activeItemID = isFMXMatrixViewMode ? focusedImageMetaID : activeToothID;

  const singleImage = IOXRayImagesInterface.length === 1;

  //TODO: implement focus mode via search params

  const updateActiveToothID = (newItemID: string) => {
    dispatch(reportsModel.actions.setToolbarActiveControl('view'));
    if (isFMXMatrixViewMode) {
      dispatch(reportsModel.actions.setFocusedMetaImageID(newItemID));

      // Update URL with new activeItemID
      if (newItemID) {
        searchParams.set('activeItemID', newItemID);
      } else {
        searchParams.delete('activeItemID');
      }
      setSearchParams(searchParams);
    } else {
      dispatch(reportsModel.actions.setActiveToothID(newItemID));

      // NOTE: It needs to properly scroll to the tooth card which appears in the DOM later than this function executes if it's just a sync call.
      if (!isMobile) {
        setTimeout(() => scrollToElementByID(newItemID, 'smooth', 'start'), 0);
      }

      if (newItemID === '') {
        dispatch(
          reportsModel.actions.setNavigationMode(FMXNavigationMode.MatrixView),
        );

        if (singleImage) {
          newItemID = IOXRayImagesInterface[0].asset.ID;
        }

        dispatch(reportsModel.actions.setFocusedMetaImageID(newItemID));

        // Remove activeItemID parameter from URL when selection is reset
        searchParams.delete('activeItemID');
        setSearchParams(searchParams);
      }
    }
  };

  if (singleImage && isFMXMatrixViewMode) {
    return null;
  }

  return (
    <RenderViewSlider
      updateActiveItem={updateActiveToothID}
      itemIDs={itemIDs as string[]}
      activeItemID={activeItemID}
      className={cn(styles.container, className)}
    />
  );
};
